import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Headphones, Upload, Save, ListChecks, FileSearch, ChevronLeft, CheckCircle2,
  Loader2, AlertTriangle, Eye, Sparkles,
} from "lucide-react";

/**
 * Admin Content Manager — three tabs:
 *  1. Listening Audio (upload .mp3 per test, see if cached)
 *  2. Answer Keys (edit answers per test)
 *  3. OCR Review Queue (review paper-based listening attempts)
 *
 * Requires student:manage permission (ADMIN / SUPER_ADMIN).
 */
const T = {
  bg: "#FCFAF3", cardBg: "#FFFFFF", cardBgWarm: "#FBF6E6",
  border: "#E5DCC0", borderSoft: "#EEE6CE",
  primary: "#F77F00", primaryDark: "#D26900", primaryGlow: "rgba(247,127,0,0.45)",
  text: "#003049", textMuted: "rgba(0,48,73,0.65)",
  success: "#16A34A", danger: "#D62828",
};

const TABS = [
  { id: "audio", label: "Listening Audio", Icon: Headphones },
  { id: "keys", label: "Answer Keys", Icon: ListChecks },
  { id: "ocr", label: "OCR Review", Icon: FileSearch },
];

function HudCorners() {
  return (
    <>
      <span className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2" style={{ borderColor: T.primary }} />
      <span className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2" style={{ borderColor: T.primary }} />
      <span className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2" style={{ borderColor: T.primary }} />
      <span className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2" style={{ borderColor: T.primary }} />
    </>
  );
}

export default function AdminContent() {
  const [tab, setTab] = useState("audio");
  return (
    <DashboardLayout>
      <div className="w-full max-w-7xl mx-auto" data-testid="admin-content-root">
        <Link to="/app/admin" className="inline-flex items-center gap-1.5 text-xs font-mono tracking-[0.25em] uppercase mb-4 hover:text-[#F77F00] transition"
          style={{ color: T.textMuted }} data-testid="admin-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Admin home
        </Link>

        <div className="mb-4">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.primary }}>// CONTENT MANAGER</div>
          <h1 className="font-display font-bold text-4xl md:text-5xl mt-1" style={{ color: T.text }}>
            Listening · Keys · OCR
          </h1>
          <p className="text-sm md:text-base mt-1" style={{ color: T.textMuted }}>
            Upload custom listening audio, fix answer keys, and review paper-based OCR submissions.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6" role="tablist">
          {TABS.map((t) => (
            <button key={t.id} role="tab" data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm font-mono text-[11px] tracking-[0.25em] uppercase transition-all"
              style={{
                background: tab === t.id ? T.primary : T.cardBg,
                color: tab === t.id ? "#fff" : T.text,
                border: `1.5px solid ${tab === t.id ? T.primary : T.border}`,
                boxShadow: tab === t.id ? `0 0 14px ${T.primaryGlow}` : "none",
              }}>
              <t.Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "audio" && <ListeningAudioTab />}
        {tab === "keys" && <AnswerKeysTab />}
        {tab === "ocr" && <OcrReviewTab />}
      </div>
    </DashboardLayout>
  );
}

// ===============================================================
// Listening Audio Tab — list tests + upload .mp3
// ===============================================================
function ListeningAudioTab() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const fileRefs = useRef({});

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/listening/tests"); setTests(r.data || []); }
    catch (e) { toast.error("Failed to load listening tests"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (tid, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    setUploading(tid);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/listening/${tid}/upload-audio`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Audio uploaded");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(null); }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm" style={{ color: T.textMuted }}><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-audio-list">
      {tests.map((t) => (
        <div key={t.id} data-testid={`audio-card-${t.id}`}
          className="relative rounded-sm p-5"
          style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}`, boxShadow: "0 4px 14px -8px rgba(0,48,73,0.10)" }}>
          <HudCorners />
          <div className="font-mono text-[9px] tracking-[0.25em] uppercase" style={{ color: T.textMuted }}>
            PART {t.part || "—"} · {t.voice || "tts"}
          </div>
          <div className="font-display font-bold text-lg mt-1" style={{ color: T.text }}>{t.title}</div>
          <div className="text-xs mt-2" style={{ color: T.textMuted }}>
            {t.question_count} questions · {t.duration_minutes ?? "?"} min
          </div>

          <div className="flex items-center gap-2 mt-3">
            {t.has_custom_audio ? (
              <span className="inline-flex items-center gap-1.5 font-mono text-[9px] tracking-[0.2em] uppercase px-2 py-1 rounded-sm"
                style={{ background: `${T.success}1A`, color: T.success }}>
                <CheckCircle2 className="w-3 h-3" /> CUSTOM
              </span>
            ) : t.audio_cached ? (
              <span className="inline-flex items-center gap-1.5 font-mono text-[9px] tracking-[0.2em] uppercase px-2 py-1 rounded-sm"
                style={{ background: `${T.primary}1A`, color: T.primary }}>
                <Sparkles className="w-3 h-3" /> TTS CACHED
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 font-mono text-[9px] tracking-[0.2em] uppercase px-2 py-1 rounded-sm"
                style={{ background: `${T.danger}1A`, color: T.danger }}>
                <AlertTriangle className="w-3 h-3" /> NO AUDIO
              </span>
            )}
          </div>

          <input ref={(el) => { fileRefs.current[t.id] = el; }} type="file" accept="audio/*" className="hidden"
            data-testid={`upload-input-${t.id}`}
            onChange={(e) => handleUpload(t.id, e.target.files?.[0])} />
          <button onClick={() => fileRefs.current[t.id]?.click()} disabled={uploading === t.id}
            data-testid={`upload-btn-${t.id}`}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm font-mono text-[10px] tracking-[0.25em] uppercase transition-all disabled:opacity-50"
            style={{ background: T.primary, color: "#fff", boxShadow: `0 0 14px ${T.primaryGlow}` }}>
            {uploading === t.id ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</> : <><Upload className="w-3.5 h-3.5" /> Upload .mp3</>}
          </button>
        </div>
      ))}
    </div>
  );
}

// ===============================================================
// Answer Keys Tab — pick a test, edit answers, save
// ===============================================================
function AnswerKeysTab() {
  const [tests, setTests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/listening/tests").then((r) => setTests(r.data || []))
      .catch(() => toast.error("Failed to load tests"));
  }, []);

  const open = async (tid) => {
    setSelected(tid);
    setDetail(null);
    try {
      const r = await api.get(`/admin/listening/${tid}/answer-keys`);
      setDetail(r.data);
    } catch { toast.error("Could not load answer keys"); }
  };

  const updateAnswer = (qid, val) => {
    setDetail((d) => ({ ...d, questions: d.questions.map((q) => q.id === qid ? { ...q, answer: val } : q) }));
  };

  const save = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const answers = (detail.questions || []).map((q) => ({ id: q.id, answer: String(q.answer ?? "") }));
      const r = await api.patch(`/admin/listening/${selected}/answer-keys`, { answers });
      toast.success(`Saved · ${r.data.updated} answers updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Test list */}
      <div className="lg:col-span-1">
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase mb-2" style={{ color: T.textMuted }}>// PICK A TEST</div>
        <div className="space-y-1.5 max-h-[560px] overflow-y-auto pr-1" data-testid="admin-keys-list">
          {tests.map((t) => (
            <button key={t.id} onClick={() => open(t.id)}
              data-testid={`keys-test-${t.id}`}
              className="w-full text-left px-3 py-2.5 rounded-sm transition-all"
              style={{
                background: selected === t.id ? `${T.primary}1A` : T.cardBg,
                border: `1px solid ${selected === t.id ? T.primary : T.borderSoft}`,
              }}>
              <div className="font-display font-semibold text-sm" style={{ color: T.text }}>{t.title}</div>
              <div className="font-mono text-[9px] tracking-[0.2em] uppercase mt-0.5" style={{ color: T.textMuted }}>
                PART {t.part || "—"} · {t.question_count} Q
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Editor */}
      <div className="lg:col-span-2">
        {!detail ? (
          <div className="rounded-sm p-10 text-center" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
            <ListChecks className="w-10 h-10 mx-auto mb-3" style={{ color: T.primary }} />
            <div className="font-display font-bold text-xl" style={{ color: T.text }}>Select a test</div>
            <div className="text-sm mt-1" style={{ color: T.textMuted }}>Pick one from the list to edit its answer keys.</div>
          </div>
        ) : (
          <div className="relative rounded-sm p-5" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }} data-testid="admin-keys-editor">
            <HudCorners />
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: T.textMuted }}>EDITING</div>
                <div className="font-display font-bold text-xl" style={{ color: T.text }}>{detail.title}</div>
              </div>
              <button onClick={save} disabled={saving} data-testid="keys-save-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm font-mono text-[10px] tracking-[0.25em] uppercase transition-all disabled:opacity-50"
                style={{ background: T.primary, color: "#fff", boxShadow: `0 0 14px ${T.primaryGlow}` }}>
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </button>
            </div>

            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
              {(detail.questions || []).map((q, i) => (
                <div key={q.id} data-testid={`keys-row-${q.id}`}
                  className="grid grid-cols-[40px_1fr_180px] gap-3 items-center px-3 py-2 rounded-sm"
                  style={{ background: i % 2 ? T.cardBgWarm : "transparent", border: `1px solid ${T.borderSoft}` }}>
                  <div className="font-mono text-[10px] tracking-[0.2em]" style={{ color: T.textMuted }}>Q{i + 1}</div>
                  <div>
                    <div className="text-sm" style={{ color: T.text }}>{q.prompt}</div>
                    <div className="font-mono text-[9px] tracking-[0.2em] uppercase mt-0.5" style={{ color: T.textMuted }}>
                      {q.type}{q.options ? ` · ${q.options.join(" / ")}` : ""}
                    </div>
                  </div>
                  <input value={q.answer ?? ""} onChange={(e) => updateAnswer(q.id, e.target.value)}
                    data-testid={`keys-input-${q.id}`}
                    className="px-3 py-2 rounded-sm text-sm font-mono"
                    style={{ background: "#fff", border: `1.5px solid ${T.border}`, color: T.text }}
                    placeholder="answer…" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ===============================================================
// OCR Review Queue Tab
// ===============================================================
function OcrReviewTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/ocr/queue?limit=50"); setItems(r.data || []); }
    catch { toast.error("Failed to load OCR queue"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const review = async (attemptId, status) => {
    try {
      await api.post(`/admin/ocr/${attemptId}/review`, { review_status: status });
      toast.success(`Marked ${status}`);
      await load();
      setOpen(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Review failed");
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm" style={{ color: T.textMuted }}><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-3" data-testid="admin-ocr-list">
      {items.length === 0 ? (
        <div className="rounded-sm p-10 text-center" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
          <FileSearch className="w-10 h-10 mx-auto mb-3" style={{ color: T.primary }} />
          <div className="font-display font-bold text-xl" style={{ color: T.text }}>No paper-based attempts yet</div>
          <div className="text-sm mt-1" style={{ color: T.textMuted }}>OCR submissions will appear here for QA review.</div>
        </div>
      ) : items.map((it) => (
        <div key={it.attempt_id} data-testid={`ocr-row-${it.attempt_id}`}
          className="relative rounded-sm p-4" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display font-semibold text-base" style={{ color: T.text }}>
                {it.user?.name || "—"} <span className="text-xs font-mono" style={{ color: T.textMuted }}>· {it.user?.email}</span>
              </div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase mt-0.5" style={{ color: T.textMuted }}>
                {it.test_id} · BAND {Number(it.band || 0).toFixed(1)} · {it.correct}/{it.total} · {new Date(it.completed_at).toLocaleString()}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 rounded-sm font-mono text-[9px] tracking-[0.2em] uppercase"
                style={{
                  background: it.review_status === "approved" ? `${T.success}1A` :
                              it.review_status === "rejected" ? `${T.danger}1A` :
                              it.review_status === "corrected" ? `${T.primary}1A` :
                              T.cardBgWarm,
                  color: it.review_status === "approved" ? T.success :
                         it.review_status === "rejected" ? T.danger :
                         it.review_status === "corrected" ? T.primary :
                         T.textMuted,
                }}>
                {it.review_status}
              </span>
              <button onClick={() => setOpen(open === it.attempt_id ? null : it.attempt_id)}
                data-testid={`ocr-toggle-${it.attempt_id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm font-mono text-[10px] tracking-[0.25em] uppercase"
                style={{ background: T.cardBgWarm, border: `1px solid ${T.border}`, color: T.text }}>
                <Eye className="w-3 h-3" /> {open === it.attempt_id ? "Hide" : "View"}
              </button>
              <button onClick={() => review(it.attempt_id, "approved")}
                data-testid={`ocr-approve-${it.attempt_id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm font-mono text-[10px] tracking-[0.25em] uppercase"
                style={{ background: T.success, color: "#fff" }}>
                Approve
              </button>
              <button onClick={() => review(it.attempt_id, "rejected")}
                data-testid={`ocr-reject-${it.attempt_id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm font-mono text-[10px] tracking-[0.25em] uppercase"
                style={{ background: T.danger, color: "#fff" }}>
                Reject
              </button>
            </div>
          </div>

          {open === it.attempt_id && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[280px] overflow-y-auto"
              data-testid={`ocr-detail-${it.attempt_id}`}>
              {(it.per_question || []).map((pq) => (
                <div key={pq.n} className="px-3 py-2 rounded-sm" style={{ background: T.cardBgWarm, border: `1px solid ${T.borderSoft}` }}>
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-[10px] tracking-[0.2em]" style={{ color: T.textMuted }}>Q{pq.n}</div>
                    {pq.is_correct
                      ? <CheckCircle2 className="w-3.5 h-3.5" style={{ color: T.success }} />
                      : <AlertTriangle className="w-3.5 h-3.5" style={{ color: T.danger }} />}
                  </div>
                  <div className="text-xs mt-1" style={{ color: T.text }}>
                    <strong>OCR:</strong> {pq.your_answer || "—"}
                  </div>
                  <div className="text-xs" style={{ color: T.textMuted }}>
                    <strong>Key:</strong> {String(pq.correct_answer)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
